import React, { useEffect, useMemo, useState } from 'react';
import ChartCard from './ChartCard';
import CardDateFilter from './CardDateFilter';
import { fetchMetric } from './graphData';

/**
 * One graph. It renders from the page-wide batch until the card is given its
 * own range, at which point it refetches only itself from /graphs/:metric and
 * stops following the page filter.
 *
 * `showRangeFilter={false}` hides the per-card range (the dashboard always shows
 * data till date); `note` is appended to the subtitle.
 */
export default function GraphCard({
  card,
  batch,
  failed = {},
  loading,
  onDashboard,
  onToggleDashboard,
  dashboardDisabled,
  onWebsite,
  onToggleWebsite,
  websiteDisabled,
  websiteTitle,
  showRangeFilter = true,
  note,
}) {
  const [override, setOverride] = useState(null);
  const [own, setOwn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ownError, setOwnError] = useState('');

  const sourceKey = card.sources.join(',');

  useEffect(() => {
    if (!override) {
      setOwn(null);
      setOwnError('');
      return undefined;
    }

    let cancelled = false;
    setBusy(true);
    setOwnError('');

    Promise.all(card.sources.map((key) => fetchMetric(key, override)))
      .then((rows) => {
        if (!cancelled) setOwn(rows);
      })
      .catch((error) => {
        if (cancelled) return;
        setOwn(null);
        setOwnError(error?.response?.data?.message || error?.message || 'Could not load this range');
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });

    return () => {
      cancelled = true;
    };
    // card.sources is rebuilt each render, so the joined key is the real dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override, sourceKey]);

  const resolved = useMemo(() => {
    if (override && own) return own;
    return card.sources.map((key) => batch[key]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [override, own, batch, sourceKey]);

  // A card is never dropped for having no data. It renders empty and says why,
  // so "nothing in this range" stays distinguishable from "this graph is gone".
  const view = card.render(resolved);
  const serverReason = card.sources.map((key) => failed[key]).find(Boolean);
  const reason = ownError || serverReason;
  const subtitle = view.subtitle || card.subtitle;

  return (
    <ChartCard
      title={card.title}
      subtitle={note ? [subtitle, note].filter(Boolean).join(' · ') : subtitle}
      labels={view.labels}
      datasets={view.datasets}
      valueFormatter={view.valueFormatter}
      loading={override ? busy : loading}
      height={card.height}
      onDashboard={onDashboard}
      onToggleDashboard={onToggleDashboard}
      dashboardDisabled={dashboardDisabled}
      onWebsite={onWebsite}
      onToggleWebsite={onToggleWebsite}
      websiteDisabled={websiteDisabled}
      websiteTitle={websiteTitle}
      emptyMessage={reason ? 'This graph could not be built' : undefined}
      emptyHint={reason || undefined}
      toolbar={showRangeFilter ? <CardDateFilter value={override} onChange={setOverride} busy={busy} /> : null}
    >
      {view.node}
    </ChartCard>
  );
}
